import { Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { getSelectorByIdentifier } from '../../../store/selectors';
import { MatSelectChange } from '@angular/material/select';
import { SelectField } from '../../../store/models/field-models';
import { extractFieldErrorMessages } from '../utils';
import { ValidationRuleType } from '../../../store/models/validator-models';
import { FieldUtilsService } from '../field-utils.service';
import { SelectableOption, SelectableValue } from '../../../store/models/selectables';
import { BasicInfoCommonFieldIds } from '../../../store/configs/common.models';
import { Mp2BasicInfoFieldId } from '../../../store/configs/triton/base-config-models';

@Component({
    selector: 'app-select-field',
    templateUrl: './select-field.component.html',
    styleUrls: ['./select-field.component.scss'],
})
export class SelectFieldComponent implements OnInit {
    @Input() field: SelectField<SelectableOption, SelectableValue>;
    @Input() control: FormControl;
    @Input() fieldId: string;

    options$: Observable<SelectableOption[]>;

    constructor(private store: Store, private translate: TranslateService, public fieldUtils: FieldUtilsService) {}

    ngOnInit(): void {
        // Initialize options for select fields
        if (this.field?.optionsSelector) {
            if (typeof this.field.optionsSelector === 'string') {
                this.options$ = this.store.select(getSelectorByIdentifier(this.field.optionsSelector));
            } else {
                this.options$ = of(this.field.optionsSelector);
            }
        }
    }

    get isVisible(): boolean {
        return this.field?.visible !== false;
    }

    get isRequired(): boolean {
        return this.field?.validators?.some((v) => v.type === ValidationRuleType.Required) || false;
    }

    get isRequiredStatusField(): boolean {
        const isStatusField =
            this.field?.id === BasicInfoCommonFieldIds.Status || this.field?.id === Mp2BasicInfoFieldId.Status;

        return isStatusField && this.isRequired;
    }

    get hasError(): boolean {
        return this.control?.invalid && (this.control?.touched || this.control?.dirty);
    }

    /**
     * Gets the option value using valueMapper if provided, otherwise falls back to default properties
     */
    getValue(option: SelectableOption): SelectableValue {
        return this.field.valueMapper(option);
    }

    /**
     * Gets the display text using displayMapper if provided, otherwise falls back to default properties
     */
    getDisplayText(option: SelectableOption): string {
        return this.field.displayMapper(option);
    }

    onChange(event: MatSelectChange, options): void {
        const selectedOption = options.find((o) => this.field.valueMapper(o) === event.value);
        if (this.field.events?.onChange) {
            this.field.events.onChange(selectedOption).forEach((action) => this.store.dispatch(action));
        }
    }

    get errorMessages(): string[] {
        return extractFieldErrorMessages(this.control, this.field, this.translate);
    }
}
